import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

import lessonLink0511 from "../lesson-11/lesson";
import lessonLink0522 from "../lesson-22/lesson";
const linkLesson0511 = getLinkForCourseLesson("53e2fab0-edf6-4634-a20e-ae0cdfe73cba", lessonLink0511.id);
const linkLesson0522 = getLinkForCourseLesson("53e2fab0-edf6-4634-a20e-ae0cdfe73cba", lessonLink0522.id);

const theory = `<h3 class=sectionHead>What will you learn in this lesson?</h3>
<p>In this lesson, recommendations for good acoustics will be given for closed spaces built for different purposes (lecture rooms, living spaces, concert halls...) regarding the:
<ul class=itemize1>
   <li class=itemize><strong><span class=cmbx-10>tentative environmental noise levels</span></strong> with respect to temporal structure and spectral content of noise,
   <li class=itemize><strong><span class=cmbx-10>airborne and impact sound insulation</span></strong> of partitions required for obtaining the stipulated indoor sound pressure levels, and
   <li class=itemize><strong><span class=cmbx-10>room acoustic parameters parameters</span></strong> calculated from the impulse response, such as the reverberation time, early decay time, clarity of music and speech, definition, and other parameters such as <strong><span class=cmbx-10>volume per seat</span></strong>.
</ul>
<p>
   At the beginning, requirements will be given for <strong><span class=cmbx-10>environmental noise levels</span></strong> in indoor spaces depending on their use (lecturing, performance spaces). The basic parameters used for describing the temporal structure and the spectral content of environmental noise are: different time- and frequency-weighted instantaneous sound pressure level, the equivalent A-weighted sound pressure level, the A-weighted maximum sound pressure level, the C-weighted peak sound pressure level, and the equivalent sound pressure levels in octave or 1/3-octave bands. These parameters are used for describing the environmental noise generated by different kinds of indoor and outdoor sources. Care must be taken to prevent this noise to find its way into a space that should be as noise-free as possible.<br class=newline>The lesson also gives an overview of the recommended <strong><span class=cmbx-10>sound insulation </span><span class=cmbx-10>parameters (airborne and impact)</span></strong> which define how well a space is isolated from outdoor and indoor noise, the most common being the apparent sound reduction index, the standardized level difference and the normalized impact sound pressure levels. Firstly, the basic requirements for airborne and impact sound insulation are given for common dwellings. Special requirements are given for partitions between different types of rooms (e.g. the partition between an engine room and a dwelling).<br class=newline>The block diagram of the lesson is shown in <a href=# data-img-src=${config.ASSETS_URL}/05/12/theory/figure-1.svg data-img-alt="Figure 1: Block diagram of the lesson.">Figure 1</a>.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/12/theory/figure-1.svg alt="Figure 1: Block diagram of the lesson."><figcaption class=caption>Figure 1. Block diagram of the lesson.</figcaption></figure>
<h3 class=sectionHead>Introduction</h3>
<p><strong><span class=cmbx-10>The process of acoustic design of any room</span></strong> has to consider different parameters depending on the intended use of the room (living space, conference hall, office, working room, performance space…).<br class=newline>The first criterion to be addressed is <strong><span class=cmbx-10>the environmental noise level</span></strong>. Any audible noise that has no connection with the event that is going on in a given space (classical concert in a concert hall, recording session in a studio, a speaker giving a talk in a lecture room, a person that wants to sleep and rest undisturbed) is to be avoided.<br class=newline>Unwanted noise can come from external traffic (rail, road, air, marine) or from adjacent rooms (neighbourhood noise, ball impacts, footsteps). A source of noise within a building can be the air conditioning system, or different kinds of machinery such as elevators, heating or cooling systems, and even water flowing through pipes and drains. The parameters used for quantifying the environmental noise levels are given in the first section of the lesson.<br class=newline>To maintain low levels of environmental noise in the room of interest, <strong><span class=cmbx-10>building </span><span class=cmbx-10>elements (walls, windows, doors, overall facade, ceiling, floors)</span></strong> with appropriate airborne and impact sound insulation quality have to be chosen. <strong><span class=cmbx-10>Frequency-dependent values</span></strong> of parameters that describe the quality of sound insulation should be considered in detail before turning to the <strong><span class=cmbx-10>single-number </span><span class=cmbx-10>values</span></strong> of these parameters.<br class=newline>To achieve good speech intelligibility and/or enhance the musical experience, acoustic conditions in the room must be addressed and improved through acoustic treatment (absorbers, diffusors), if necessary. To describe the acoustics of a room, a number of parameters has been developed that stem from the <strong><span class=cmbx-10>impulse response</span></strong> of the room.<br class=newline>The basic parameter which can be obtained from impulse response is the reverberation time, which is considered in detail regarding the measurement procedures and the estimation according to the Sabine equation.
<h3 class=sectionHead>Environmental noise levels</h3>
<p>
   The environmental noise levels can be measured both <strong><span class=cmbx-10>outdoors and indoors</span></strong> in some performance spaces such as lecture rooms, conference halls, living rooms or bedrooms. In general, sound can be recorded by microphones in different places, thus converting the sound pressure into the corresponding voltage signal (representation of a
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-punc>.</mo>
      <mi mathvariant=italic>wav</mi>
   </math>
   file) on the sound card of a computer, or using a sound level meter or a mobile phone. However, measurements and recordings using mobile phones should be avoided because such a measurement system is not calibrated, and the sensitivity of the microphone is generally not known across the entire frequency range.<br class=newline>The waveform of sound pressure needs to be recorded with a <strong><span class=cmbx-10>calibrated </span><span class=cmbx-10>measurement system</span></strong> with appropriate sampling frequency to cover the entire audio frequency range (20 Hz-20 kHz). All environmental noise parameters can then be determined from the recorded waveform. An example of the sound pressure waveform of background noise alone and with the source turned on is shown in <a href=# data-img-src=${config.ASSETS_URL}/05/12/theory/figure-2.png data-img-alt="Figure 2: The waveform of sound pressure recorded in the time domain with a calibrated measurement system (only background noise in the room vs. a noise source turned on somewhere in the neighbourhood).">Figure 2</a>. Each sample of sound pressure in Figure 2 is obtained by recording with a condenser microphone with known <strong><span class=cmbx-10>sensitivity (50 mV/Pa)</span></strong>, at the <strong><span class=cmbx-10>sampling </span><span class=cmbx-10>frequency</span></strong> of 44.1 kHz. The sound pressure waveform is shown on the display of the measuring instrument.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/12/theory/figure-2.png alt="Figure 2: The waveform of sound pressure recorded in the time domain with a calibrated measurement system (only background noise in the room vs. a noise source turned on somewhere in the neighbourhood)."><figcaption class=caption>Figure 2. The waveform of sound pressure recorded in the time domain with a calibrated measurement system (only background noise in the room vs. a noise source turned on somewhere in the neighbourhood).</figcaption></figure>
<p>
   The
   <strong>
      <span class=cmbx-10>instantaneous sound pressure level</span>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>L</mi>
         <mo class=MathClass-open>(</mo>
         <mi>t</mi>
         <mo class=MathClass-close>)</mo>
      </math>
   </strong>
   can be found knowing the sound pressure signal in the time domain, using the integration constant according to Equation 1.<br class=newline>The instantaneous sound pressure levels are obtained by squaring the sound pressure and integrating that in time. The time constant of integration ensures that the current pressure samples have the greatest weight, while those that appear later are given less weight in the integration process. The <strong><span class=cmbx-10>integration </span><span class=cmbx-10>constant RC</span></strong> takes values with respect to the required response time of the measurement system, depending on the temporal structure of the sound event under investigation: IMPULSE = 35 ms, FAST = 125 ms, SLOW = 1 s.
<table class=equation>
   <tr>
      <td style="font-size: 11px">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>L</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mn>10</mn>
            <mi mathvariant=italic>log</mi>
            <mo class=MathClass-open>(</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>RC</mi>
               </mrow>
            </mfrac>
            <msubsup>
               <mrow>
                  <mo>∫</mo>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi>∞</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msubsup>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mo class=MathClass-open>(</mo>
                  <mi>τ</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
               </mrow>
            </mfrac>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mo class=MathClass-open>(</mo>
                        <mi>τ</mi>
                        <mo class=MathClass-bin>−</mo>
                        <mi>t</mi>
                        <mo class=MathClass-close>)</mo>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>RC</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </msup>
            <mi mathvariant=italic>dτ</mi>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   The choice of the <strong><span class=cmbx-10>FAST or SLOW integration constant</span></strong> has an influence on the instantaneous environmental noise parameters. This is presented through an example shown in <a href=# data-img-src=${config.ASSETS_URL}/05/12/theory/figure-3.png data-img-alt="Figure 3: A sound event in form of a rectangular pulse analyzed using different integration constants.">Figure 3</a>. A short rectangular pressure pulse with the magnitude of 1 Pa magnitude is analyzed using both integration constants.<br class=newline>With the integration constant set to FAST, the <strong><span class=cmbx-10>changes in sound </span><span class=cmbx-10>pressure level</span></strong> are <strong><span class=cmbx-10>more pronounced</span></strong>, and the maximum value of the instantaneous pressure level is closer to the true value of 94 dB (=
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>20 </mn>
      <mi mathvariant=italic>log</mi>
      <mo class=MathClass-open>(</mo>
      <mn>1</mn>
      <mi > Pa</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>20 </mn>
      <mi> μPa</mi>
   </math>
   ), as shown. The initial value of -50 dB depends on the minimum number representation in the memory of the instrument.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/12/theory/figure-3.png alt="Figure 3: A sound event in form of a rectangular pulse analyzed using different integration constants."><figcaption class=caption>Figure 3. A sound event in form of a rectangular pulse analyzed using different integration constants.</figcaption></figure>
<p>
   <strong><span class=cmbx-10>The equivalent continuous value of the sound pressure</span></strong> over a defined time interval is obtained by integrating the squared values of the sound pressure over the entire interval
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
   </math>
   , as defined with Eq 2. The minimum measurement interval is set to 5 to 10 min. If the considered sound source is stable and produces stationary sound, the defined time interval can be separated into several shorter ones. The minimum measurement time interval is usually determined by observing the long-term temporal structure and assessing the cyclic nature of the environmental noise generated by the investigated sound source.
<table class=equation>
   <tr>
      <td style="font-size: 15px">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>eq</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mn>10</mn>
            <mi mathvariant=italic>log</mi>
            <mo class=MathClass-open>(</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>RC</mi>
               </mrow>
            </mfrac>
            <msubsup>
               <mrow>
                  <mo>∫</mo>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mi>T</mi>
               </mrow>
            </msubsup>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mo class=MathClass-open>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
               </mrow>
            </mfrac>
            <mi mathvariant=italic>dτ</mi>
             <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   Other important parameters that can be obtained from the pressure signal in the time domain through integration are:
   <strong>
      <span class=cmbx-10>maximum instantaneous sound pressure level </span><span class=cmbx-10>(</span>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>L</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>max</mi>
            </mrow>
         </msub>
      </math>
      <span class=cmbx-10>), peak sound </span><span class=cmbx-10>pressure level (</span>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>L</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>peak</mi>
            </mrow>
         </msub>
      </math>
      <span class=cmbx-10>)</span>
   </strong>
   in a defined integration time, which is usually set to 1 s. For impulse events the integration time can be shortened even to 10 ms. <strong><span class=cmbx-10>The peak sound </span><span class=cmbx-10>pressure level</span></strong> can be read from the pressure signal in the time domain, and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Lmax</mi>
   </math>
   is usually obtained by using the FAST integration constant in a defined time frame.<br class=newline>
   <strong>
      <span class=cmbx-10>The maximum sound pressure level</span>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>L</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>max</mi>
            </mrow>
         </msub>
      </math>
   </strong>
   corresponds to the maximum current level
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>L</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   in the observed time interval
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
   </math>
   (at the frame level) and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>peak</mi>
         </mrow>
      </msub>
   </math>
   corresponds to the maximum current sound pressure level at the samples shown in <a href=# data-img-src=${config.ASSETS_URL}/05/12/theory/figure-2.png data-img-alt="Figure 2: The waveform of sound pressure recorded in the time domain with a calibrated measurement system (only background noise in the room vs. a noise source turned on somewhere in the neighbourhood).">Figure 2</a>. The parameters obtained from the pressure signal shown in <a href=# data-img-src=${config.ASSETS_URL}/05/12/theory/figure-2.png data-img-alt="Figure 2: The waveform of sound pressure recorded in the time domain with a calibrated measurement system (only background noise in the room vs. a noise source turned on somewhere in the neighbourhood).">Figure 2</a> are shown in <a href=# data-img-src=${config.ASSETS_URL}/05/12/theory/figure-4.svg data-img-alt="Figure 4: The values of the environmental noise parameters shown over time, obtained from the recorded sound pressure waveform shown in Figure 2 using both fast and slow integration constant.">Figure 4</a> for fast and slow integration constant. The refresh rate at which the parameters are shown is 1 s, except for the instantaneous sound pressure level, for which the refresh rate corresponds to the sampling frequency of the instrument.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/12/theory/figure-4.svg alt="Figure 4: The values of the environmental noise parameters shown over time, obtained from the recorded sound pressure waveform shown in Figure 2 using both fast and slow integration constant."><figcaption class=caption>Figure 4. The values of the environmental noise parameters shown over time, obtained from the recorded sound pressure waveform shown in <a href=# data-img-src=${config.ASSETS_URL}/05/12/theory/figure-2.png data-img-alt="Figure 2: The waveform of sound pressure recorded in the time domain with a calibrated measurement system (only background noise in the room vs. a noise source turned on somewhere in the neighbourhood).">Figure 2</a> using both fast and slow integration constant.</figcaption></figure>
<p>
   The use of the slow time constant tends to smooth out the curve showing the instantaneous sound pressure level. During measurements, all unwanted sources of noise should be turned off first, so that the background noise level (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>
   ) can be obtained, as well as its equivalent sound pressure level, maximum sound pressure level and peak sound pressure level.<br class=newline>The next step is to repeat the process with the noise sources turned on.<br class=newline>This measurement procedure has to be <strong><span class=cmbx-10>repeated in several selected measurement </span><span class=cmbx-10>points chosen in the room</span></strong> according to the relevant standard that describes the measurements of indoor environmental noise (ISO 1996-2:2017). The values obtained from measurements with the source(s) turned on and off should be averaged through energetic averaging, which is described in <a href=${linkLesson0522} target="_blank">Lesson 5.22</a> on sound insulation measurements. The background noise level is used to correct the measured total SPL level (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
   </math>
   ) which describes both the noise of the source and the background noise. The correction is performed according to Eq. 3, yielding the noise level (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>L</mi>
   </math>
   ) produced only by the operation of the source(s) of interest. This correction needs to be performed for all measurement parameters for which the difference of the total level and the level of background noise is larger than 3 dB.
<table class=equation>
   <tr>
      <td style="font-size: 14px">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>L</mi>
            <mo class=MathClass-rel>=</mo>
            <mn>10</mn>
            <mi mathvariant=italic>log</mi>
            <mo class=MathClass-open>(</mo>
            <mn>1</mn>
            <msup>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mo class=MathClass-open>(</mo>
               </mrow>
            </msup>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>10</mn>
               </mrow>
            </mfrac>
                    <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>−</mo>
            <mn>1</mn>
            <msup>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mo class=MathClass-open>(</mo>
               </mrow>
            </msup>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>res</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mn>10</mn>
               </mrow>
            </mfrac>
            <mo class=MathClass-close>)</mo>
             <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   These environmental noise parameters can be further evaluated by <strong><span class=cmbx-10>passing the </span><span class=cmbx-10>measured pressure signals</span></strong> through the <strong><span class=cmbx-10>filters with different frequency </span><span class=cmbx-10>weightings</span></strong>, as shown in <a href=# data-img-src=${config.ASSETS_URL}/05/12/theory/figure-5.svg data-img-alt="Figure 5: Frequency weightings used on pressure signals (top); octave and one-third octave bands used for representation of the measured sound pressure in the frequency domain (bottom).">Figure 5</a> (top). The pressure signals can also be passed through <strong><span class=cmbx-10>octave or one-third octave band filters</span></strong> with predefined center frequencies and bandwidth, as shown in <a href=# data-img-src=${config.ASSETS_URL}/05/12/theory/figure-5.svg data-img-alt="Figure 5: Frequency weightings used on pressure signals (top); octave and one-third octave bands used for representation of the measured sound pressure in the frequency domain (bottom).">Figure 5</a> (bottom).
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/12/theory/figure-5.svg alt="Figure 5: Frequency weightings used on pressure signals (top); octave and one-third octave bands used for representation of the measured sound pressure in the frequency domain (bottom)."><figcaption class=caption>Figure 5. Frequency weightings used on pressure signals (top); octave and one-third octave bands used for representation of the measured sound pressure in the frequency domain (bottom).</figcaption></figure>
<p>
   <strong><span class=cmbx-10>The A-weighting</span></strong> tends to imitate the response of the human hearing system at low loudness level, thus yielding single-number values that offer a better agreement with the human perception of loudness of sound. More weight is given to the frequency bands in which the human hearing system is more sensitive. The C-weighting imitates the behaviour of the human hearing system at high loudness level and gives more weight on the low-frequency content than the A-weighting does.<br class=newline>The values of different sound pressure level parameters obtained for the signal shown in <a href=# data-img-src=${config.ASSETS_URL}/05/12/theory/figure-2.png data-img-alt="Figure 2: The waveform of sound pressure recorded in the time domain with a calibrated measurement system (only background noise in the room vs. a noise source turned on somewhere in the neighbourhood).">Figure 2</a> with A-weighting included are shown in <a href=# data-img-src=${config.ASSETS_URL}/05/12/theory/figure-6.png data-img-alt="Figure 6: The values of environmental noise parameters obtained with A-weighting for the recorded signal shown in Figure 2.">Figure 6</a>.<br class=newline>Generally, the A-weighted parameters have lower values than the Z-weighted ones (no weighting) due to the frequency content of the signal. Any frequency weighting can be implemented in the time domain by convolving the pressure signal (a frame of the signal in memory buffer) with the impulse response of the weighting filter (using the same buffer length). Alternatively, frequency weighting can be implemented in the frequency domain by knowing the frequency content of the recorded signal, i.e. by knowing the level values in octave or one-third octave bands, and the corresponding attenuation of the weighting filter in those bands.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/12/theory/figure-6.png alt="Figure 6: The values of environmental noise parameters obtained with A-weighting for the recorded signal shown in Figure 2."><figcaption class=caption>Figure 6. The values of environmental noise parameters obtained with A-weighting for the recorded signal shown in <a href=# data-img-src=${config.ASSETS_URL}/05/12/theory/figure-2.png data-img-alt="Figure 2: The waveform of sound pressure recorded in the time domain with a calibrated measurement system (only background noise in the room vs. a noise source turned on somewhere in the neighbourhood).">Figure 2</a>.</figcaption></figure>
<p>
   <a href=# data-img-src=${config.ASSETS_URL}/05/12/theory/figure-7.svg data-img-alt="Figure 7: Zero weighting on the middle part of the unwanted source signal shown in figure 2. and A-weighting of the same part of the signal.">Figure 7</a> shows the spectral content of the pressure signal shown in <a href=# data-img-src=${config.ASSETS_URL}/05/12/theory/figure-2.png data-img-alt="Figure 2: The waveform of sound pressure recorded in the time domain with a calibrated measurement system (only background noise in the room vs. a noise source turned on somewhere in the neighbourhood).">Figure 2</a> in one-third octave bands, i.e. the content of the noise with the noise source switched on. The unweighted (Z-weighting) spectrum is shown on top, and the A-weighted spectrum is shown at the bottom of the figure.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/12/theory/figure-7.svg alt="Figure 7: Zero weighting on the middle part of the unwanted source signal shown in figure 2. and A-weighting of the same part of the signal."><figcaption class=caption>Figure 7. Zero weighting on the middle part of the unwanted source signal shown in <a href=# data-img-src=${config.ASSETS_URL}/05/12/theory/figure-2.png data-img-alt="Figure 2: The waveform of sound pressure recorded in the time domain with a calibrated measurement system (only background noise in the room vs. a noise source turned on somewhere in the neighbourhood).">Figure 2</a>. and A-weighting of the same part of the signal.</figcaption></figure>
<p>
   To address <strong><span class=cmbx-10>the differences in noise annoyance</span></strong> that arise depending on the noise source, sound character, time of day, etc., <strong><span class=cmbx-10>adjustments</span></strong> are added to <strong><span class=cmbx-10>the measured or </span><span class=cmbx-10>predicted noise levels</span></strong>, as stipulated by national legislation. If there is a prominent <strong><span class=cmbx-10>tonal component</span></strong> in the noise or the noise is impulsive, additional tonal adjustement
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mi>T</mi>
         </mrow>
      </msub>
   </math>
   and/or impulse adjustment
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mi>I</mi>
         </mrow>
      </msub>
   </math>
   have to be added to the measured A-weighted equivalent sound pressure level
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>A</mi>
            <mo class=MathClass-punc>,</mo>
            <mi mathvariant=italic>eq</mi>
         </mrow>
      </msub>
   </math>
   to get the final
   <strong>
      <span class=cmbx-10>rating value </span>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>L</mi>
            </mrow>
            <mrow>
               <mi>R</mi>
               <mo class=MathClass-punc>,</mo>
               <mi mathvariant=italic>Aeq</mi>
            </mrow>
         </msub>
      </math>
      <span class=cmbx-10>or </span><span class=cmbx-10>sound descriptors </span>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>L</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>day</mi>
            </mrow>
         </msub>
      </math>
      <span class=cmbx-10>,</span>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>L</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>evening</mi>
            </mrow>
         </msub>
      </math>
      <span class=cmbx-10>,</span>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>L</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>night</mi>
            </mrow>
         </msub>
      </math>
      <span class=cmbx-10>,</span>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>L</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>den</mi>
            </mrow>
         </msub>
      </math>
   </strong>
   . <strong><span class=cmbx-10>Sound descriptors</span></strong> are calculated from the known operating time of the source and the equivalent sound pressure levels, according to the ISO 1996-2:2017.<br class=newline>
   <strong>
      <span class=cmbx-10>The tonal adjustment </span>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>K</mi>
            </mrow>
            <mrow>
               <mi>T</mi>
            </mrow>
         </msub>
      </math>
   </strong>
   is estimated from the level difference between the one-third octave band where a prominent tone is located and the averaged sound pressure levels in adjacent bands (see ISO 1996-1:2016).<br class=newline>
   <strong>
      <span class=cmbx-10>The impulse adjustment </span>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>K</mi>
            </mrow>
            <mrow>
               <mi>I</mi>
            </mrow>
         </msub>
      </math>
   </strong>
   is added to measured values if impulse signals are present in the measured signal. When the sound produced by an impulsive source is so low that it cannot be separated from the sound produced by other sources, or if the impulses are so infrequent that they do not affect the result, then these impulses are not considered. <strong><span class=cmbx-10>The common impulse adjustment is 5 dB</span></strong> when the impulsive events occur at a rate equal to or higher than the minimum rate specified by responsible authorities. Typically, this rate ranges from one event every few seconds to one event every couple of minutes.<br class=newline>A recommendation for equivalent sound pressure levels in closed spaces states that outdoor sound sources should not increase the level of indoor noise. This is often very difficult to estimate due to variability of background noise.<br class=newline>The tentative typical values of the A-weighted equivalent sound pressure levels in different types of spaces are given in <a href=# data-img-src=${config.ASSETS_URL}/05/12/theory/table-1.svg data-img-alt="Table 1: Recommended values for A-weighted sound pressure levels in different type of rooms.">Table 1</a>. The measurement result to be compared with the tentative value is obtained from several measurements of equal or unequal duration. The results should be statistically averaged and both the experimental measurement uncertainty
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>u</mi>
   </math>
   and the overall experimental measurement uncertainty
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>U</mi>
   </math>
   should be found. They are usually given with cover interval (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>k</mi>
   </math>
   ), one or two sided interval of confidence and the probability (usually 95 %) of finding the true value. If the equivalent level is measured in unfurnished rooms, an additional correction due to larger reverberation time (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
   </math>
   ) should be added to the measured equivalent sound pressure levels, according to Eq. 4, thus obtaining the standardized equivalent sound pressure level.
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>A</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi mathvariant=italic>eq</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi mathvariant=italic>nT</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>A</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi mathvariant=italic>eq</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <mn>10</mn>
            <mi mathvariant=italic>log</mi>
            <mo class=MathClass-open>(</mo>
            <mfrac>
               <mrow>
                  <mi>T</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>T</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(4)
</table>
<p>
   The recommended value of reverberation time for speech ranges from
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>0</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>4</mn>
      <mo class=MathClass-bin>−</mo>
      <mn>0</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>6</mn>
      <mi>s</mi>
   </math>
   in small rooms, so the average value for
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   = 0.5 s is taken as reference. If the room is furnished, actual reverberation time in the room should be measured.
<div class=table>
   <p>
      <figure class=float>
      <figcaption class=caption>Table 1. Recommended values for A-weighted sound pressure levels in different type of rooms.</figcaption>
      <img src=${config.ASSETS_URL}/05/12/theory/table-1.svg alt="Table 1: Recommended values for A-weighted sound pressure levels in different type of rooms."></figure>
</div>
<p>In addition to the equivalent sound pressure levels some tentative values for maximum sound pressure level in measurement intervals when equipment (air conditioning, ventilation, heating) is turned on are given in <a href=#x1-30122>Table 2</a>.
<div class=table>
   <p>
      <figure class=float>
      <figcaption class=caption>Table 2. Recommended values for <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>L</mi></mrow><mrow><mi>A</mi><mo class=MathClass-punc>,</mo><mi mathvariant=italic>max</mi></mrow></msub></math>.</figcaption>
      <img src=${config.ASSETS_URL}/05/12/theory/table-2.svg alt="Table 2: Recommended values for <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>L</mi></mrow><mrow><mi>A</mi><mo class=MathClass-punc>,</mo><mi mathvariant=italic>max</mi></mrow></msub></math>.">
      </figure>
</div>
<h5 class=subsubsectionHead>Noise weighting curves</h5>
<p>
   Indication of A-weighting levels in the room <strong><span class=cmbx-10>does not give any information on </span><span class=cmbx-10>the specific frequency components present in the noise signal</span></strong> that may be the cause of non-compliance with noise regulations. For the purpose of acoustic design, it is more convenient to use <strong><span class=cmbx-10>weighting curves</span></strong> which define a spectrum of band levels in terms of single numbers. There is a possibility to use five sets of single number weighting curves: Noise Rating (NR curve), Noise Criteria (NC), Room Criteria (RC), Balanced Noise Criteria (NCB) and Room Noise Criteria (RNC).<br class=newline>In this subsection only the <strong><span class=cmbx-10>Noise Rating curve (NR)</span></strong> is explained in more detail as it is proposed by the International Standards Organization (ISO) and intended for rating the environmental and industrial noise levels. The sound pressure levels in octave bands <strong><span class=cmbx-10>should be measured and plotted</span></strong> against the noise rating curves. An example of measured sound pressure levels of noise emitted by an air ventilation system in an office is shown in <a href=# data-img-src=${config.ASSETS_URL}/05/12/theory/figure-8.png data-img-alt="Figure 8: Measured noise levels of source noise and background noise, and corrected noise levels of the source, all plotted against the noise rating curves.">Figure 8</a>. The noise levels are measured for background noise (noise source switched off) and the noise of the source switched on as unweighted octave-band levels. The correction of noise levels of the source is done with regard to the levels of background noise. All this data is plotted against the noise rating curves. To determine the single-number NR rating, the lowest noise rating curve that is still above all the measured octave-band levels must be found. If the most prominent octave-band level falls between two curves, the nearest integer value is then used to determine the overall noise rating.<br class=newline>In <a href=# data-img-src=${config.ASSETS_URL}/05/12/theory/figure-8.png data-img-alt="Figure 8: Measured noise levels of source noise and background noise, and corrected noise levels of the source, all plotted against the noise rating curves.">Figure 8</a> we have shown the noise rating curves and the unweighted sound pressure levels in octave bands <strong><span class=cmbx-10>measured with and without the ventilation system </span><span class=cmbx-10>turned on</span></strong> in the room. The measured sound pressure levels are averaged both in time and in space (five measurements, each lasting five minutes). The duration of total measurement should be at least five minutes to obtain representative samples, if the sound generated by the sound source is stationary, according to the standard ISO 1996-2:2017. <strong><span class=cmbx-10>The background noise level</span></strong> should be measured immediately before or after the measurement of the noise generated by the investigated noise source. The measurement results obtained for the source must be corrected to account for the influence of background noise. This correction is made in all frequency bands of interest, and it is usually small or even negligible due to the source being much louder than the background noise. During the measurement process, it is not uncommon to obtain higher noise levels of background noise in low-frequency bands than the ones produced by the investigated noise source. This happens due to the high variability of low-frequency sources of background noise.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/12/theory/figure-8.png alt="Figure 8: Measured noise levels of source noise and background noise, and corrected noise levels of the source, all plotted against the noise rating curves."><figcaption class=caption>Figure 8. Measured noise levels of source noise and background noise, and corrected noise levels of the source, all plotted against the noise rating curves.</figcaption></figure>
<p>In the example shown in <a href=# data-img-src=${config.ASSETS_URL}/05/12/theory/figure-8.png data-img-alt="Figure 8: Measured noise levels of source noise and background noise, and corrected noise levels of the source, all plotted against the noise rating curves.">Figure 8</a>, the obtained noise rating is NR30 with the ventilation turned off. With the ventilation switched on, the noise rating increases to NR40.<br class=newline>
<p>Some recommended values for noise rating curves are given in <a href=# data-img-src=${config.ASSETS_URL}/05/12/theory/table-3.svg data-img-alt="Table 3: Recommended Noise Rating values for different types of spaces.">Table 3</a>.
<div class=table>
   <p>
      <figure class=float>
      <figcaption class=caption>Table 3. Recommended Noise Rating values for different types of spaces.</figcaption>
      <img src=${config.ASSETS_URL}/05/12/theory/table-3.svg alt="Table 3: Recommended Noise Rating values for different types of spaces.">
      </figure>
</div>
<p>
   The example shows that the environmental noise parameters in the investigated space are appropriate with the ventilation turned off (except
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>A</mi>
            <mo class=MathClass-punc>,</mo>
            <mi mathvariant=italic>max</mi>
         </mrow>
      </msub>
   </math>
   ). On the other hand, the parameters are significantly increased when the air handling unit is turned on.<br class=newline>Depending on the legislation, in some countries the overall noise level is observed (background noise included), whereas in others only the noise level of the source is observed (sound descriptor for the source).
<h3 class=sectionHead>Requirements for sound insulation parameters</h3>
<p>
   By knowing the recommended values of indoor sound pressure levels expressed with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>A</mi>
            <mo class=MathClass-punc>,</mo>
            <mi mathvariant=italic>eq</mi>
         </mrow>
      </msub>
   </math>
   ,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>RA</mi>
            <mo class=MathClass-punc>,</mo>
            <mi mathvariant=italic>eq</mi>
         </mrow>
      </msub>
   </math>
   or sound descriptors, or the maximum A-weighted value
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>A</mi>
            <mo class=MathClass-punc>,</mo>
            <mi mathvariant=italic>max</mi>
         </mrow>
      </msub>
   </math>
   for a given room, we can estimate the necessary values of airborne and impact sound insulation parameters for partitions between the source room and the protected room. National building standards stipulate requirements for some values of sound insulation parameters depending on the position and function of a partition in a building. The minimum requirements for the apparent sound reduction index (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
   </math>
   ) or the standardized level difference (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>D</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>nT</mi>
         </mrow>
      </msub>
   </math>
   ) as descriptors of airborne sound insulation are given in <a href=# data-img-src=${config.ASSETS_URL}/05/12/theory/table-4.svg data-img-alt="Table 4: Minimum requirements for airborne sound insulation in different countries for two types of housing.">Table 4</a> for partitions between dwellings in multi-storey housing and in row housing. A detailed explanation of these parameters is given in <a href=${linkLesson0522} target="_blank">Lesson 5.22</a>.
<div class=table>
   <p>
      <figure class=float>
      <figcaption class=caption>Table 4. Minimum requirements for airborne sound insulation in different countries for two types of housing.</figcaption>
      <img src=${config.ASSETS_URL}/05/12/theory/table-4.svg alt="Table 4: Minimum requirements for airborne sound insulation in different countries for two types of housing.">
      </figure>
</div>
<p>The countries with cold climate usually have stricter requirements for sound insulation performance than the Mediterranean countries, in which these requirements are looser.<br class=newline>The minimum requirements for the values of descriptors of impact sound insulation are given in <a href=# data-img-src=${config.ASSETS_URL}/05/12/theory/table-5.svg data-img-alt="Table 5: Minimum requirements for impact sound insulation in different countries for two types of housing.">Table 5</a>, again for partitions between dwellings in multi-storey housing and in row housing.
<div class=table>
   <p>
      <figure class=float>
      <figcaption class=caption>Table 5. Minimum requirements for impact sound insulation in different countries for two types of housing.</figcaption>
      <img src=${config.ASSETS_URL}/05/12/theory/table-5.svg alt="Table 5: Minimum requirements for impact sound insulation in different countries for two types of housing."></figure>
</div>
<p>Additional requirements on airborne and impact sound insulation depending on the function of the partition are shown in <a href=# data-img-src=${config.ASSETS_URL}/05/12/theory/table-6.svg data-img-alt="Table 6: Requirements on airborne and impact sound insulation depending on partition type.">Table 6</a>. These values may differ from one country to the next.
<div class=table>
   <p>
      <figure class=float>
      <figcaption class=caption>Table 6. Requirements on airborne and impact sound insulation depending on partition type.</figcaption>
      <img src=${config.ASSETS_URL}/05/12/theory/table-6.svg alt="Table 6: Requirements on airborne and impact sound insulation depending on partition type."></figure>
</div>
<p><strong><span class=cmbx-10>Important: Higher values of airborne sound insulation parameters and </span><span class=cmbx-10>lower values of impact sound insulation levels are better.</span></strong>
<h3 class=sectionHead>Requirements for room acoustics parameters</h3>
<p>
   Room acoustic parameters are determined mainly from <strong><span class=cmbx-10>room impulse responses</span></strong> recorded at several positions within the room. The impulse response of a room is defined as a form of temporal evaluation of sound pressure observed at a given position in a room as a result of a Dirac pulse being emitted by a source positioned at another position in the room. In practice, it is impossible to generate and emit true Dirac pulses, but short transient sounds (e.g. a gunshot) are a reasonable approximation that can be used for measurement purposes. However, in large rooms the energy produced at low frequencies by such a source can be insufficient, so the source must be chosen carefully to overcome this problem.<br class=newline>An alternative measurement technique is to use a frequency sweeping signal or other forms of deterministic, flat-spectrum signal such as the minimum length sequence or periodic noise. The measured frequency response is transformed back to the time domain, thereby obtaining the impulse response. An example of a room impulse response has been recorded in a room with the volume of V=116
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mn>3</mn>
         </mrow>
      </msup>
   </math>
   , and is shown in <a href=# data-img-src=${config.ASSETS_URL}/05/12/theory/figure-9.svg data-img-alt="Figure 9: Impulse response and the resulting energy-time curve.">Figure 9</a>. The energy decay curve calculated from the impulse response and used for estimation of reverberation time is shown in the bottom part of <a href=# data-img-src=${config.ASSETS_URL}/05/12/theory/figure-9.svg data-img-alt="Figure 9: Impulse response and the resulting energy-time curve.">Figure 9</a>.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/12/theory/figure-9.svg alt="Figure 9: Impulse response and the resulting energy-time curve."><figcaption class=caption>Figure 9. Impulse response and the resulting energy-time curve.</figcaption></figure>
<p>
   The room impulse response such as the one shown in <a href=# data-img-src=${config.ASSETS_URL}/05/12/theory/figure-9.svg data-img-alt="Figure 9: Impulse response and the resulting energy-time curve.">Figure 9</a> is the starting point for the determination of room acoustical parameters such as
   <strong>
      <span class=cmbx-10>reverberation time</span>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>T</mi>
      </math>
      <span class=cmbx-10>, early decay </span><span class=cmbx-10>time </span>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi mathvariant=italic>EDT</mi>
      </math>
      <span class=cmbx-10>, clarity</span>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>C</mi>
         <mn>80</mn>
      </math>
      <span class=cmbx-10>, definition</span>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>D</mi>
         <mn>50</mn>
      </math>
      <span class=cmbx-10>, center time</span>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>T</mi>
            </mrow>
            <mrow>
               <mi>S</mi>
            </mrow>
         </msub>
      </math>
      <span class=cmbx-10>, strength</span>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>G</mi>
      </math>
      <span class=cmbx-10>, and lateral </span><span class=cmbx-10>energy fraction </span>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi mathvariant=italic>LF</mi>
      </math>
   </strong>
   , which are defined in <a href=${linkLesson0511} target="_blank">Lesson 5.11</a>.<br class=newline>The choice of measurement positions for measuring the impulse response of a room should follow the guidelines given in ISO 3382-1:2009, and the results should be averaged across all measurement positions. An impulse response can be analyzed by filtering it using an octave or one-third octave filter bank with standardized center frequencies. The room acoustical parameters can then be found in each octave or one-third octave band within the frequency range covered by the filter bank.
<h4 class=subsectionHead>Reverberation time</h4>
<p>
   Every closed space <strong><span class=cmbx-10>adds echo and reverberation to the original ”direct” </span><span class=cmbx-10>sound</span></strong> from different source emitted by a source. Echo is an undesirable room characteristic and we can easily detect it from the impulse response or the resulting energy time curve (ETC).<br class=newline><strong><span class=cmbx-10>Reverberation time T (fundamental parameter in room acoustics</span><span class=tcbx-1000>〉</span></strong> is defined as the time required for the space-averaged sound energy density in a closed space (room, enclosure) to decrease by 60 dB after the source emission has stopped.<br class=newline><strong><span class=cmbx-10>Reverberation time</span></strong> can be and usually is evaluated based on <strong><span class=cmbx-10>dynamic </span><span class=cmbx-10>range smaller than 60 dB</span></strong>, as it is very difficult in practice to achieve 60 dB of dynamic range during measurements. The obtained value is then extrapolated to the decay time that corresponds to the decay of 60 dB, according to ISO 3382-2. Reverberation time can be derived from the 20-dB or 30-dB decay of sound energy, namely, from the time interval between -5 dB and -25 (-35) dB on the decay curve, and is labelled accordingly as T20 or T30, respectively.<br class=newline>The Early Decay Time (EDT) is derived in the same manner, but the upper limit on the decay curve is 0 dB (maximum value), and the lower limit is -10 dB. This parameter correlates very well with the perceived reverberance of a room. In practice, the interval between -1 dB and -10 dB is used for the estimation of the early decay time.<br class=newline>The
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
      <mn>10</mn>
   </math>
   reverberation time uses the portion of the decay curve with the upper limit at -5 dB to remove any fluctuations. The lower limit is set to -15 dB, but at least 10 dB of headroom above the noise floor must be available. Therefore, at least 25 dB (5+10+10) of dynamic range is needed in the given situation to be able to calculate T10. Based on the ranges defined above for
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
      <mn>20</mn>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
      <mn>30</mn>
   </math>
   , the required dynamic range for the calculation of these parameters is 35 and 45 dB, respectively.<br class=newline>For very smooth decay of sound in a room, the values of all the described parameters should be equal.<br class=newline>Reverberation can enhance sound perception in a room as it provides loudness enhancement, a sense of involvement and an impression of space. On the other hand, it has a negative effect on source localization and it lowers speech intelligibility if too pronounced. The energy-time curve shown in <a href=# data-img-src=${config.ASSETS_URL}/05/12/theory/figure-9.svg data-img-alt="Figure 9: Impulse response and the resulting energy-time curve.">Figure 9</a> makes it possible to use the interval from -5 dB to -25 dB to estimate the reverberation time. The 20-dB decay happens in 1 second, which gives the extrapolated value of T20=3*1s=3s.<br class=newline>
<p>
   For the purpose of acoustic impulse response measurements in a room, <strong><span class=cmbx-10>source </span><span class=cmbx-10>positions</span></strong> should be chosen to match the positions of actual sound sources in the room (speakers in conference rooms, lecture rooms, theaters, churches, etc.). A minimum of two source positions shall be used. The height of the acoustic centre of the source should be 1.5 m above the floor. Microphone positions should be at positions that cover the audience area, i.e. they have to be chosen to sample the entire space. Microphone positions should be chosen carefully (minimum height of 1.2 m above the floor), to capture the major influences that are likely to cause deviations and outliers in measured values.<br class=newline>The tentative values for reverberation time regarding the volume of the room and its purpose are given in <a href=# data-img-src=${config.ASSETS_URL}/05/12/theory/figure-10.png data-img-alt="Figure 10: Recommended values of reverberation time depending on the volume of the room and its purpose.">Figure 10</a>.(Long)
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/12/theory/figure-10.png alt="Figure 10: Recommended values of reverberation time depending on the volume of the room and its purpose."><figcaption class=caption>Figure 10. Recommended values of reverberation time depending on the volume of the room and its purpose.</figcaption></figure>
<p>
   The recommended values described above refer to the mid-frequency reverberation time. As reverberation time is frequency-dependant, its desired change over frequency is proposed as well. For rooms intended for music performance, longer reverberation time at low frequencies is desired, compared to mid-frequency reverberation time. For rooms that host speech-based events, constant reverberation time over frequency is desired. This is illustrated in <a href=# data-img-src=${config.ASSETS_URL}/05/12/theory/figure-11.png data-img-alt="Figure 11: The desired frequency dependence of reverberation time.">Figure 11</a>.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/12/theory/figure-11.png alt="Figure 11: The desired frequency dependence of reverberation time."><figcaption class=caption>Figure 11. The desired frequency dependence of reverberation time.</figcaption></figure>
<p>
   For non-occupied concert and multipurpose halls, a range of <strong><span class=cmbx-10>appropriate values</span></strong> can be defined for other parameters that can be calculated from room impulse response. The tentative values for these parameters are given in <a href=# data-img-src=${config.ASSETS_URL}/05/12/theory/table-7.svg data-img-alt="Table 7: Tentative values for room acoustical parameters derived from the impulse response.">Table 7</a> for room volume less than 25 000
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mn>3</mn>
         </mrow>
      </msup>
   </math>
   . The tentative values (averaged in octave bands of 500 Hz and 1 kHz) can be found in ISO 3382-1:2010. In last columns the averaged measurement results for 1. lecture rectangular room (V=246
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mn>3</mn>
         </mrow>
      </msup>
   </math>
   ) with some acoustic treatment with absorbers on the back and side wall is shown together with smaller rectangular lecture room without any acoustic treatment (V=146
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mn>3</mn>
         </mrow>
      </msup>
   </math>
   )
<div class=table>
   <p>
      <figure class=float>
      <figcaption class=caption>Table 7. Tentative values for room acoustical parameters derived from the impulse response.</figcaption>
      <img src=${config.ASSETS_URL}/05/12/theory/table-7.svg alt="Table 7: Tentative values for room acoustical parameters derived from the impulse response."></figure>
</div>
<p>In the design process of performance spaces, <strong><span class=cmbx-10>volume per seat</span></strong> is also an important parameter. Its tentative values are given in <a href=# data-img-src=${config.ASSETS_URL}/05/12/theory/table-8.svg data-img-alt="Table 8: Recommended volume per seat for different spaces.">Table 8</a> (Long) for different types of spaces.
<div class=table>
   <p>
      <figure class=float>
      <figcaption class=caption>Table 8. Recommended volume per seat for different spaces.</figcaption>
      <img src=${config.ASSETS_URL}/05/12/theory/table-8.svg alt="Table 8: Recommended volume per seat for different spaces."></figure>
</div>
<p>More detailed specification of parameters for lecture rooms (speech) are given in <a href=# data-img-src=${config.ASSETS_URL}/05/12/theory/table-9.svg data-img-alt="Table 9: Recommended values of room acoustical parameters for venues that host speech-based events, according to DIN 18041:2004-05.">Table 9</a>, according to the DIN 18041:2004-05 standard.
<div class=table>
   <p>
      <figure class=float>
      <figcaption class=caption>Table 9. Recommended values of room acoustical parameters for venues that host speech-based events, according to DIN 18041:2004-05.</figcaption>
      <img src=${config.ASSETS_URL}/05/12/theory/table-9.svg alt="Table 9: Recommended values of room acoustical parameters for venues that host speech-based events, according to DIN 18041:2004-05."></figure>
</div>
<h3 class=sectionHead>Conclusion</h3>
<p>It is evident that a number of parameters need to be considered to ensure the acoustic quality of a space. The tentative values for maximum permissible equivalent sound pressure levels in rooms are given. Based on this data, partition elements with appropriate sound insulation parameters can be chosen to meet these demands. To ensure optimal acoustic conditions in the room itself, whether it is a space dedicated to music performance, a space primarily for speech-based events, or a mixed-purpose space, the required values of room acoustics parameters need to be achieved through careful acoustic design.
<h3 class=likesectionHead>References</h3>
<div class=thebibliography>
<dl>
   <dt>[1]
   <dd>ISO 1996-1:2016, Acoustics – Description, measurement and assessment of environmental noise – Part 1: Basic quantities and assessment procedures
   <dt>[2]
   <dd>ISO 1996-2:2017, Acoustics - Description, measurement and assessment of environmental noise – Part 2: Determination of sound pressure levels
   <dt>[3]
   <dd>David A. Bies, Colin H. Hansen: Engineering Noise Control: Theory and Practice, Fifth Edition, 2009
   <dt>[4]
   <dd>ISO 3382-1:2010: Acoustics - Measurement of room acoustic parameters - Part 1: Performance spaces
   <dt>[5]
   <dd>D. Rossing: Springer Handbook of Acoustics, Springer Verlag, 2007
   <dt>[6]
   <dd>M. Long: Architectural Acoustics, Academic Press, 2006
   <dt>[7]
   <dd>M. Barron: Auditorium Acoustics and Architectural Design, Spon Press, 2009
</dl>
</div>`;

export default theory;
